import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import type { DetailTab, UseAnalyticalDatasetDetailPageResult } from '../types';
import { useAdInlineProcess } from './useAdInlineProcess';
import { useAdRouteParams } from './useAdRouteParams';
import { useAnalyticalDatasetEditLock } from './useAnalyticalDatasetEditLock';
import { useHydrateAnalyticalDatasetDefinition } from './useHydrateAnalyticalDatasetDefinition';
import { useSelectedSourceCohort } from './useSelectedSourceCohort';
import { analyticalDatasetKeys } from '../api/queryKeys';
import { useAnalyticalDatasetProfileQuery } from '../api/useAnalyticalDatasetProfileQuery';
import { selectCohorts } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';

const TAB_QUERY_PARAM = 'pageTab';

const VALID_TABS: readonly DetailTab[] = ['definition', 'results'];

// An unrecognized `?pageTab=` value resolves to 'definition' rather than a
// blank tab.
const parseTab = (raw: string | null): DetailTab => {
  if (raw && (VALID_TABS as readonly string[]).includes(raw)) {
    return raw as DetailTab;
  }
  return 'definition';
};

export const useAnalyticalDatasetDetailPage = (): UseAnalyticalDatasetDetailPageResult => {
  const { formatMessage } = useIntl();

  const { projectId, analyticalDatasetProfileId, analyticalDatasetVersionId } = useAdRouteParams();

  // Seed the definition store here (page level, always mounted) so the Process
  // button's source-cohort gate works on any tab — a refresh straight onto the
  // Results tab otherwise leaves the store empty and falsely reports "no cohort".
  useHydrateAnalyticalDatasetDefinition();

  const { data: profileEnvelope, isLoading: isProfileLoading } = useAnalyticalDatasetProfileQuery({
    projectId,
    analyticalDatasetProfileId,
  });
  const profile = profileEnvelope?.data;

  // When the profile poll observes PROCESSING -> terminal, refresh the list so its
  // status pill isn't stranded at PROCESSING (mirrors cohort's useCohortProfileData).
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<string | undefined>();
  const latestStatus = profile?.latestStatus;
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = latestStatus;
    if (prevStatus === 'PROCESSING' && latestStatus !== 'PROCESSING') {
      queryClient.invalidateQueries({ queryKey: analyticalDatasetKeys.list(projectId) });
      // A finished run means new results — drop every cached results page so
      // the Results tab refetches against the new execution.
      queryClient.invalidateQueries({
        queryKey: analyticalDatasetKeys.profileResults(analyticalDatasetProfileId),
      });
    }
  }, [latestStatus, projectId, analyticalDatasetProfileId, queryClient]);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams.get(TAB_QUERY_PARAM));

  const onTabChange = useCallback(
    (_event: React.SyntheticEvent, next: DetailTab) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === 'definition') {
            updated.delete(TAB_QUERY_PARAM);
          } else {
            updated.set(TAB_QUERY_PARAM, next);
          }
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const pageTitle = profile?.name ?? formatMessage({ id: 'analyticalDatasetDetail.fallbackTitle' });

  // TODO: cleanup with skip_process_confirmation_modal ff
  const isInlineProcessEnabled = Boolean(useFeatureFlagSplit('skip_process_confirmation_modal'));
  const { runProcess, isProcessSubmitting, processError } = useAdInlineProcess();

  // Flag off: the Process button opens a modal that drives the run + in-flight
  // UI (mirrors cohort's process modal); the mutation lives in `useAdProcessModal`.
  // Flag on: Process fires `runProcess` immediately, no modal.
  const [isProcessModalOpen, setProcessModalOpen] = useState(false);
  const onProcess = useCallback(() => {
    if (isInlineProcessEnabled) {
      runProcess();
      return;
    }
    setProcessModalOpen(true);
  }, [isInlineProcessEnabled, runProcess]);
  const onCloseProcessModal = useCallback(() => setProcessModalOpen(false), []);

  // The Download button opens a modal that fetches + saves the export CSVs. Only
  // enabled once the latest run is PROCESSED — there are no files otherwise.
  const [isDownloadModalOpen, setDownloadModalOpen] = useState(false);
  const onDownload = useCallback(() => setDownloadModalOpen(true), []);
  const onCloseDownloadModal = useCallback(() => setDownloadModalOpen(false), []);
  const canDownload = latestStatus === 'PROCESSED';

  // Can't process a dataset with no source cohort. Read the (staged) selection
  // from the definition store — same source the definition tab + meta row use.
  const cohorts = useAnalyticalDatasetDefinitionStore(selectCohorts);

  // Processing is also locked for non-owners and while a run is in flight
  // (same gate that locks the definition edits).
  const { isEditDisabled, isProcessing, isOwner } = useAnalyticalDatasetEditLock();
  const hasCohort = cohorts.length > 0;

  // The backend rejects processing with a 409 while the attached cohort is
  // DRAFT (unprocessed) — mirror that here so the button explains itself
  // instead of round-tripping to the error. Permissive (treated as processed)
  // while the join is still loading or hasn't resolved, so the button doesn't
  // flash disabled on page load; the backend check remains authoritative.
  //
  // Not flag-gated: a DRAFT source cohort is only reachable with
  // `asset_edit_while_in_use` on (the picker filters to PROCESSED otherwise,
  // and a cohort in use is edit-locked while the flag is off), so with the flag
  // off `selectedCohort` is always PROCESSED/FINAL and this stays inert.
  const { selectedCohort, isCohortListLoading } = useSelectedSourceCohort();
  const isCohortProcessed =
    isCohortListLoading || !selectedCohort
      ? true
      : selectedCohort.latestStatus === CohortDefinitionStatus.PROCESSED ||
        selectedCohort.latestStatus === CohortDefinitionStatus.FINAL;

  const canProcess = hasCohort && !isEditDisabled && isCohortProcessed;

  const processTooltip = isProcessing
    ? formatMessage({ id: 'analyticalDatasetDetail.processTooltip.processing' })
    : !isOwner
    ? formatMessage({ id: 'analyticalDatasetDetail.processTooltip.notOwner' })
    : !hasCohort
    ? formatMessage({ id: 'analyticalDatasetDetail.processTooltip.noCohort' })
    : !isCohortProcessed
    ? formatMessage({ id: 'analyticalDatasetDetail.processTooltip.cohortNotProcessed' })
    : undefined;

  return {
    projectId,
    analyticalDatasetProfileId,
    analyticalDatasetVersionId,
    pageTitle,
    isProfileLoading,
    activeTab,
    onTabChange,
    onProcess,
    isProcessModalOpen,
    onCloseProcessModal,
    isInlineProcessEnabled,
    isProcessSubmitting,
    processError,
    canProcess,
    processTooltip,
    onDownload,
    isDownloadModalOpen,
    onCloseDownloadModal,
    canDownload,
  };
};
